import React, { useEffect, useRef, useState } from 'react';
import { Alert, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, Typography } from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import ActionButton from '../../ActionButton';
import { exportTableToPDF } from './ExportTablePDF';
import { exportTableToExcel } from './ExportTableData';

export const validateExportRange = (start, end) => {
  const valid = date => /^\d{4}-\d{2}-\d{2}$/.test(date) && Number.isFinite(Date.parse(`${date}T00:00:00Z`))
    && new Date(`${date}T00:00:00Z`).toISOString().slice(0, 10) === date;
  if (!valid(start) || !valid(end)) return 'Choose a valid start and end date.';
  return start > end ? 'Start date must be on or before end date.' : '';
};

// Fetch a separate export snapshot; never change the report currently displayed on screen.
export default function ExportReportDialog({ format, title, defaultFileName, initialDates, columns = [], loadRows, search = '', isDark, onClose, helpText, exportPDF, scopeSummary }) {
  const [fileName, setFileName] = useState(defaultFileName);
  const startDate = initialDates?.startDate || '';
  const endDate = initialDates?.endDate || '';
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const controller = useRef(null);
  const portalAnchor = useRef(null);
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; controller.current?.abort(); }; }, []);
  const submit = async event => {
    event.preventDefault();
    if (controller.current) return;
    const validation = !fileName.trim() ? 'Enter a filename.' : validateExportRange(startDate, endDate);
    if (validation) { setError(validation); return; }
    const request = new AbortController();
    controller.current = request;
    setBusy(true);
    setError('');
    try {
      const result = await loadRows({ startDate, endDate, signal: request.signal });
      if (request.signal.aborted || !mounted.current) return;
      const term = search.trim().toLowerCase();
      const rows = term ? result.rows.filter(row => columns.some(column => String(row[column.field] ?? '').toLowerCase().includes(term))) : result.rows;
      if (!rows.length) throw new Error('No matching data for these dates and filters. Choose another range.');
      const notes = [`Report dates: ${startDate} to ${endDate}`,
        ...(scopeSummary ? [scopeSummary] : []),
        ...(term ? [`Table search: ${search}`] : []), ...(result.notes || [])];
      const options = { rows, columns, title, fileName: fileName.trim(), notes };
      if (format === 'pdf') {
        if (exportPDF) await exportPDF({ ...options, report: result.report, startDate, endDate });
        else exportTableToPDF(options);
      }
      else await exportTableToExcel(options);
      if (mounted.current) onClose();
    } catch (failure) {
      if (mounted.current && !request.signal.aborted) setError(failure.message || 'Export failed. Try a smaller date range.');
    } finally {
      controller.current = null;
      if (mounted.current) setBusy(false);
    }
  };
  return <ThemeProvider theme={createTheme({
    typography: { fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif', fontSize: 14 },
    palette: { mode: isDark ? 'dark' : 'light', primary: { main: isDark ? '#9bbaff' : '#315fca' },
      text: { primary: isDark ? '#e5edf8' : '#172b46', secondary: isDark ? '#a6b6cc' : '#586b82' } },
  })}>
    {/* Keep the portal inside Reports so its shared design tokens also style the dialog and buttons. */}
    <span ref={portalAnchor} />
    <Dialog open onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth aria-labelledby="export-report-title"
      container={() => portalAnchor.current?.closest('.reports-suite') || document.body}
      PaperProps={{ sx: {
        borderRadius: '16px', border: '1px solid var(--report-line, #dde5ee)',
        background: 'var(--report-surface, #ffffff)', color: 'var(--report-ink, #172b46)',
        boxShadow: 'var(--report-shadow)', backgroundImage: 'none',
        '& .MuiOutlinedInput-root': { borderRadius: '9px', backgroundColor: 'var(--report-raised)', fontSize: 14 },
        '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--report-line)' },
        '& input[type="date"]': { colorScheme: isDark ? 'dark' : 'light' },
      } }}>
      <form onSubmit={submit}>
        <DialogTitle id="export-report-title" sx={{ px: 3, py: 2, fontSize: 20, fontWeight: 500, borderBottom: '1px solid var(--report-line)' }}>Export {title}</DialogTitle>
        <DialogContent sx={{ px: 3, pt: '20px !important', pb: 3 }}>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField label="Filename" value={fileName} onChange={event => setFileName(event.target.value)} disabled={busy} autoFocus fullWidth required />
            <Typography variant="body2">Applied dates: {startDate || 'Not set'} to {endDate || 'Not set'}</Typography>
            {scopeSummary && <Typography variant="body2">{scopeSummary}</Typography>}
            <Typography variant="body2" color="text.secondary">To change dates or locations, close this dialog and apply your filters first.</Typography>
            {helpText && <Typography variant="caption" color="text.secondary">{helpText}</Typography>}
            {busy && <Typography role="status">Preparing export…</Typography>}
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1, borderTop: '1px solid var(--report-line)', backgroundColor: 'var(--report-raised)' }}>
          <ActionButton variant="text" disabled={busy} onClick={onClose}>Cancel</ActionButton>
          <ActionButton type="submit" disabled={busy}>{busy ? 'Preparing…' : `Export ${format === 'pdf' ? 'PDF' : 'Excel'}`}</ActionButton>
        </DialogActions>
      </form>
    </Dialog>
  </ThemeProvider>;
}
